import React from 'react';
import { Button, Modal, Tooltip } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';

/**
 * SessionWarningModal — shared session expiry warning component.
 *
 * Ported from D:\ldcbe-patient\src\Component\Shared\SessionWarningModal.js
 * and enhanced with admin portal UX additions:
 *   - Progress dots showing extensions used / max_extensions
 *   - Button disabled when max extensions reached
 *   - Colour transitions on urgency (<10 s remaining)
 */
const SessionWarningModal = ({
  isOpen,
  timeRemaining,
  onExtend,
  onDecline,
  maxExtensionsReached,
  extensionsUsed,
  maxExtensions,
}) => {
  const minutes = Math.floor(timeRemaining / 60000);
  const seconds = Math.floor((timeRemaining % 60000) / 1000);
  const isUrgent = timeRemaining > 0 && timeRemaining <= 10000;

  return (
    <Modal
      title={null}
      open={isOpen}
      footer={null}
      closable={false}
      centered
      maskClosable={false}
      width={400}
      styles={{ body: { padding: '32px 24px' } }}
    >
      <div style={{ textAlign: 'center' }}>

        {/* Step progress dots: one dot per available extension */}
        {maxExtensions > 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            {Array.from({ length: maxExtensions }).map((_, i) => {
              const isUsed    = i < extensionsUsed;
              const isCurrent = i === extensionsUsed && extensionsUsed < maxExtensions;
              return (
                <div
                  key={i}
                  title={
                    isUsed
                      ? `Extension ${i + 1} used`
                      : isCurrent
                      ? `Extension ${i + 1} available`
                      : `Extension ${i + 1} remaining`
                  }
                  style={{
                    width:           isCurrent ? 12 : 10,
                    height:          isCurrent ? 12 : 10,
                    borderRadius:    '50%',
                    backgroundColor: isUsed ? '#ef4444' : isCurrent ? '#f59e0b' : '#d1d5db',
                    border:          isCurrent ? '2px solid #f59e0b' : isUsed ? '2px solid #ef4444' : '2px solid #d1d5db',
                    boxShadow:       isCurrent ? '0 0 6px rgba(245,158,11,0.6)' : 'none',
                    animation:       isCurrent ? 'sw-pulse 1s infinite' : 'none',
                    transition:      'all 0.3s ease',
                    flexShrink:      0,
                  }}
                />
              );
            })}
            <span style={{
              fontSize:   12,
              color:      extensionsUsed >= maxExtensions ? '#ef4444' : '#6b7280',
              fontWeight: 600,
              marginLeft: 4,
            }}>
              {extensionsUsed} / {maxExtensions}
            </span>
          </div>
        )}

        {/* Clock icon */}
        <ClockCircleOutlined
          style={{
            fontSize:    '56px',
            color:       extensionsUsed >= maxExtensions ? '#ef4444' : '#f59e0b',
            marginBottom: '16px',
            animation:   isUrgent ? 'sw-pulse 1s infinite' : 'none',
          }}
        />

        {/* Title */}
        <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '4px', color: '#111827' }}>
          {extensionsUsed >= maxExtensions
            ? 'Last chance — session expiring'
            : 'Are you still there?'}
        </h3>

        {/* Subtitle — available extension indicator */}
        {extensionsUsed < maxExtensions && (
          <p style={{ fontSize: 13, color: '#9ca3af', marginBottom: 0, marginTop: 2 }}>
            Extension {extensionsUsed + 1} of {maxExtensions} available
          </p>
        )}

        {/* Countdown box */}
        <div style={{
          background:    '#f9fafb',
          border:        `1px solid ${isUrgent ? '#fca5a5' : '#e5e7eb'}`,
          borderRadius:  12,
          padding:       '16px',
          marginBottom:  '20px',
          marginTop:     '16px',
        }}>
          <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: 8, fontWeight: 500 }}>
            Your booking session will expire in
          </p>
          <div style={{
            fontSize:    36,
            fontWeight:  700,
            color:       isUrgent ? '#ef4444' : '#008385',
            fontFamily:  'monospace',
            lineHeight:  1,
            animation:   isUrgent ? 'sw-pulse 1s infinite' : 'none',
          }}>
            {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
          </div>
        </div>

        {/* Body text */}
        <p style={{
          fontSize:   14,
          color:      extensionsUsed >= maxExtensions ? '#dc2626' : '#4b5563',
          marginBottom: 24,
          fontWeight: extensionsUsed >= maxExtensions ? 500 : 400,
        }}>
          {extensionsUsed >= maxExtensions
            ? 'No more extensions available. Please complete your booking quickly.'
            : "We've reserved this slot for you. Do you need more time to complete this booking?"}
        </p>

        {/* Buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Tooltip title={extensionsUsed >= maxExtensions ? 'Maximum extensions reached' : ''}>
            <Button
              type="primary"
              size="large"
              block
              disabled={extensionsUsed >= maxExtensions}
              onClick={onExtend}
              style={{
                backgroundColor: extensionsUsed >= maxExtensions ? '#d9d9d9' : '#008385',
                borderColor:     extensionsUsed >= maxExtensions ? '#d9d9d9' : '#008385',
                color:           extensionsUsed >= maxExtensions ? '#999' : '#fff',
                height:          48,
                borderRadius:    8,
                fontSize:        16,
                fontWeight:      600,
                cursor:          extensionsUsed >= maxExtensions ? 'not-allowed' : 'pointer',
                opacity:         extensionsUsed >= maxExtensions ? 0.6 : 1,
                boxShadow:       extensionsUsed >= maxExtensions ? 'none' : '0 4px 6px -1px rgba(0,131,133,0.2)',
              }}
            >
              {extensionsUsed >= maxExtensions ? 'No extensions left' : 'Yes, extend my session'}
            </Button>
          </Tooltip>

          <Button
            size="large"
            block
            onClick={onDecline}
            style={{
              height:     40,
              borderRadius: 8,
              fontSize:   15,
              border:     'none',
              background: 'transparent',
              color:      '#6b7280',
            }}
          >
            No, let it expire
          </Button>
        </div>
      </div>

      {/* Keyframe animation — scoped to this component */}
      <style>{`
        @keyframes sw-pulse {
          0%   { transform: scale(1);    opacity: 1;   }
          50%  { transform: scale(1.05); opacity: 0.8; }
          100% { transform: scale(1);    opacity: 1;   }
        }
      `}</style>
    </Modal>
  );
};

export default SessionWarningModal;
